import React, { useState, useEffect, useCallback } from 'react';
import { proFetch, proHeaders, estPatron } from '../../utils/proApi';
import BriefEditeur from '../../components/pro/BriefEditeur';
import { Page, EnTetePage, Bloc, Etiquette, Bouton, Etat, Pastille, Mention, Vide, Montant, Toast, Icone } from '../../components/pro/ui';

// Écran Chantiers — ce qui se passe après l'acceptation d'un devis.
// Un chantier s'ouvre depuis un lead ; on y dépose les documents, on y tient
// le journal d'avancement, et on y ouvre l'accès du client.

const STATUTS = [
  { id: 'prepare',  label: 'En préparation', ton: 'attente' },
  { id: 'en_cours', label: 'En cours',       ton: 'cours' },
  { id: 'termine',  label: 'Terminé',        ton: 'fait' },
  { id: 'annule',   label: 'Annulé',         ton: 'neutre' },
];
const labelStatut = (id) => STATUTS.find(s => s.id === id)?.label || id;
const tonStatut = (id) => STATUTS.find(s => s.id === id)?.ton || 'neutre';

const fmtEur = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (ds) => ds ? new Date(`${ds}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const aujourdhui = () => new Date().toISOString().slice(0, 10);

// Lundi de la semaine contenant `d`.
function lundiDe(d) {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
}
const iso = (d) => d.toISOString().slice(0, 10);

// Charge de la semaine : qui est sur quoi, et surtout quel chantier n'a
// personne. L'écran Équipe affecte jour par jour ; ici on lit l'inverse.
function ChargeSemaine() {
  const [lundi, setLundi] = useState(() => lundiDe(new Date()));
  const [charge, setCharge] = useState(null);
  const from = iso(lundi);
  const to = iso(new Date(lundi.getTime() + 6 * 86400000));

  useEffect(() => {
    let vivant = true;
    proFetch(`/chantiers/charge?from=${from}&to=${to}`)
      .then(r => r.json())
      .then(d => { if (vivant) setCharge(d); })
      .catch(() => { if (vivant) setCharge(null); });
    return () => { vivant = false; };
  }, [from, to]);

  const decaler = (semaines) => setLundi(l => new Date(l.getTime() + semaines * 7 * 86400000));
  const libelleSemaine = `${lundi.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} → ${new Date(lundi.getTime() + 6 * 86400000).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;

  const decouverts = (charge?.chantiers || []).filter(c => c.decouvert);
  const libres = (charge?.ouvriers || []).filter(o => o.jours === 0);

  return (
    <div className="bg-slate-100/70 rounded-xl px-4 py-3.5 mb-7">
      <div className="flex items-center gap-3 mb-2.5">
        <Etiquette className="mr-auto">Charge de la semaine</Etiquette>
        <button onClick={() => decaler(-1)} className="text-slate-400 hover:text-slate-700 px-1.5" aria-label="Semaine précédente">←</button>
        <span className="text-xs font-medium text-slate-600 tabular-nums">{libelleSemaine}</span>
        <button onClick={() => decaler(1)} className="text-slate-400 hover:text-slate-700 px-1.5" aria-label="Semaine suivante">→</button>
      </div>

      {!charge ? (
        <p className="text-xs text-slate-400">Chargement…</p>
      ) : charge.chantiers.length === 0 ? (
        <p className="text-xs text-slate-400">Aucun chantier sur cette semaine.</p>
      ) : (
        <ul className="divide-y divide-slate-200/70">
          {charge.chantiers.map(c => (
            <li key={c.ref} className="flex items-center gap-3 py-2">
              <Pastille ton={c.decouvert ? 'alerte' : 'fait'} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {c.client ? `${c.client.prenom || ''} ${(c.client.nom || '').toUpperCase()}`.trim() : c.ref}
                  <span className="text-xs text-slate-400 font-normal ml-2">{c.ref}</span>
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {c.decouvert
                    ? (c.dateDebut ? `démarre le ${fmtDate(c.dateDebut)} — personne d'affecté` : 'aucune date, personne d\u2019affecté')
                    : c.ouvriers.map(o => `${o.nom} (${o.jours} j)`).join(' · ')}
                </p>
              </div>
              <span className={`shrink-0 text-xs font-bold tabular-nums ${c.decouvert ? 'text-red-600' : 'text-slate-600'}`}>
                {c.joursHomme} j·h
              </span>
            </li>
          ))}
        </ul>
      )}

      {charge && (decouverts.length > 0 || libres.length > 0) && (
        <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-200/70">
          {decouverts.length > 0 && <span className="text-red-600 font-medium">{decouverts.length} chantier{decouverts.length > 1 ? 's' : ''} sans personne. </span>}
          {libres.length > 0 && <>Disponibles : {libres.map(o => o.nom).join(', ')}.</>}
          {' '}<a href="/pro/equipe" className="text-orange-600 font-medium">Affecter →</a>
        </p>
      )}
    </div>
  );
}

export default function ProChantiers() {
  const [chantiers, setChantiers] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [ouvert, setOuvert] = useState(null);      // ref du chantier déplié
  const [detail, setDetail] = useState(null);
  const [leadsOuvrables, setLeadsOuvrables] = useState([]);
  const [message, setMessage] = useState(null);

  const toast = (texte, type = 'ok') => { setMessage({ texte, type }); setTimeout(() => setMessage(null), 4000); };

  const charger = useCallback(async () => {
    try {
      const r = await proFetch('/chantiers');
      setChantiers(await r.json());
    } catch { /* la liste se recharge au prochain rendu */ }
    finally { setChargement(false); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  // Leads acceptés qui n'ont pas encore de chantier : la liste des candidats.
  const chargerLeadsOuvrables = useCallback(async () => {
    try {
      const r = await proFetch('/leads?status=accepte');
      const leads = await r.json();
      const refsOuvertes = new Set(chantiers.map(c => c.ref));
      setLeadsOuvrables((Array.isArray(leads) ? leads : leads?.leads || []).filter(l => !refsOuvertes.has(l.ref)));
    } catch { setLeadsOuvrables([]); }
  }, [chantiers]);
  useEffect(() => { chargerLeadsOuvrables(); }, [chargerLeadsOuvrables]);

  // Recharge le détail sans toucher à l'ouverture du panneau : après un dépôt
  // de document ou une étape, le panneau doit rester là où on travaille.
  const rafraichirDetail = useCallback(async (ref) => {
    try {
      const r = await proFetch(`/chantiers/${ref}`);
      setDetail(await r.json());
      charger();
    } catch (e) { toast(e.message, 'err'); }
  }, [charger]);

  const ouvrirDetail = async (ref) => {
    if (ouvert === ref) { setOuvert(null); setDetail(null); return; }
    setOuvert(ref); setDetail(null);
    await rafraichirDetail(ref);
  };

  const creer = async (ref) => {
    try {
      const r = await proFetch('/chantiers', { method: 'POST', body: JSON.stringify({ ref }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur');
      toast(`Chantier ${ref} ouvert`);
      await charger();
      setOuvert(ref);
      rafraichirDetail(ref);
    } catch (e) { toast(e.message, 'err'); }
  };

  const majChantier = async (ref, champs) => {
    try {
      const r = await proFetch(`/chantiers/${ref}`, { method: 'PATCH', body: JSON.stringify(champs) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur');
      setDetail(d => ({ ...d, ...data }));
      setChantiers(cs => cs.map(c => c.ref === ref ? { ...c, ...data } : c));
    } catch (e) { toast(e.message, 'err'); }
  };

  if (chargement) return <div className="p-8 text-slate-400">Chargement…</div>;

  return (
    <Page>
      <EnTetePage titre="Chantiers" sous="Les devis acceptés qui ont pris corps : documents, avancement, accès client.">
        <span className="text-sm text-slate-400 tabular-nums">
          {chantiers.length} ouvert{chantiers.length > 1 ? 's' : ''}
        </span>
      </EnTetePage>

      <ChargeSemaine />

      {leadsOuvrables.length > 0 && (
        <div className="border border-slate-200 bg-white rounded-xl px-4 py-3.5 mb-5">
          <Etiquette className="mb-2">
            {leadsOuvrables.length} devis accepté{leadsOuvrables.length > 1 ? 's' : ''} sans chantier
          </Etiquette>
          <div className="flex flex-wrap gap-2">
            {leadsOuvrables.map(l => (
              <Bouton key={l.ref} taille="petit" icone="plus" onClick={() => creer(l.ref)}>
                {l.ref} — {l.client?.prenom} {l.client?.nom}
              </Bouton>
            ))}
          </div>
        </div>
      )}

      {chantiers.length === 0 ? (
        <Bloc>
          <Vide>
            Aucun chantier ouvert.
            <span className="block text-slate-300 mt-1">Passez un devis en « accepté » dans le pipeline, il apparaîtra ici.</span>
          </Vide>
        </Bloc>
      ) : (
        <div className="space-y-2">
          <Etiquette className="mb-2">Chantiers ouverts</Etiquette>
          {chantiers.map(c => (
            <div key={c.ref} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <button onClick={() => ouvrirDetail(c.ref)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50/70 transition-colors flex items-center gap-3">
                <Pastille ton={tonStatut(c.statut)} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">
                    {c.client ? `${c.client.prenom || ''} ${(c.client.nom || '').toUpperCase()}`.trim() : c.ref}
                  </p>
                  <p className="text-xs text-slate-400 truncate">
                    {c.ref} · {labelStatut(c.statut)}
                    {c.dateDebut && ` · début ${fmtDate(c.dateDebut)}`}
                    {c.nbDocuments > 0 && ` · ${c.nbDocuments} doc.`}
                  </p>
                </div>
                {c.montantTTC > 0 && <Montant valeur={c.montantTTC} className="text-sm font-semibold text-slate-700 shrink-0" />}
                {!c.accesActif && <Mention>sans accès</Mention>}
                <Icone nom="fleche" className="w-4 h-4 text-slate-300 shrink-0" />
              </button>

              {ouvert === c.ref && (
                detail ? <Detail chantier={detail} onMaj={majChantier} onRecharger={() => rafraichirDetail(c.ref)} toast={toast} />
                       : <p className="px-4 py-6 text-sm text-slate-400">Chargement du chantier…</p>
              )}
            </div>
          ))}
        </div>
      )}

      <Toast message={message} />
    </Page>
  );
}

function Detail({ chantier, onMaj, onRecharger, toast }) {
  const [enCours, setEnCours] = useState(false);
  const [etape, setEtape] = useState({ date: aujourdhui(), titre: '', detail: '' });

  const deposer = async (fichier) => {
    if (!fichier) return;
    setEnCours(true);
    try {
      const form = new FormData();
      form.append('fichier', fichier);
      form.append('nom', fichier.name);
      // FormData impose son propre Content-Type (avec le boundary) : on reprend
      // les en-têtes d'authentification sans celui-là.
      const { 'Content-Type': _ignore, ...entetes } = proHeaders();
      const r = await fetch(`/api/pro/chantiers/${chantier.ref}/documents`, { method: 'POST', headers: entetes, body: form });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur');
      toast(`${data.nom} déposé — interne pour l'instant`);
      onRecharger();
    } catch (e) { toast(e.message, 'err'); }
    finally { setEnCours(false); }
  };

  const basculerVisible = async (doc) => {
    try {
      await proFetch(`/chantiers/${chantier.ref}/documents/${doc._id}`, {
        method: 'PATCH', body: JSON.stringify({ visibleClient: !doc.visibleClient }),
      });
      onRecharger();
    } catch (e) { toast(e.message, 'err'); }
  };

  const supprimerDoc = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.nom} » ? Le fichier est effacé définitivement.`)) return;
    try {
      await proFetch(`/chantiers/${chantier.ref}/documents/${doc._id}`, { method: 'DELETE' });
      toast('Document supprimé');
      onRecharger();
    } catch (e) { toast(e.message, 'err'); }
  };

  const telecharger = async (doc) => {
    try {
      const r = await proFetch(`/chantiers/${chantier.ref}/documents/${doc._id}/url`);
      const { url } = await r.json();
      window.open(url, '_blank', 'noopener');
    } catch (e) { toast(e.message, 'err'); }
  };

  const ajouterEtape = async () => {
    if (!etape.titre.trim()) return;
    try {
      const r = await proFetch(`/chantiers/${chantier.ref}/etapes`, { method: 'POST', body: JSON.stringify(etape) });
      if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
      setEtape({ date: aujourdhui(), titre: '', detail: '' });
      toast('Étape ajoutée — visible par le client');
      onRecharger();
    } catch (e) { toast(e.message, 'err'); }
  };

  const envoyerAcces = async () => {
    const email = chantier.client?.email;
    if (!window.confirm(`Envoyer le lien d'accès à ${email || 'ce client'} ?`)) return;
    try {
      const r = await proFetch(`/chantiers/${chantier.ref}/acces/envoyer`, { method: 'POST', body: JSON.stringify({}) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur');
      toast(`Lien envoyé à ${data.envoyeA}`);
      onRecharger();
    } catch (e) { toast(e.message, 'err'); }
  };

  const revoquer = async () => {
    if (!window.confirm('Révoquer l’accès ? Tous les liens déjà envoyés cesseront de fonctionner.')) return;
    try {
      await proFetch(`/chantiers/${chantier.ref}/acces/revoquer`, { method: 'POST' });
      toast('Accès révoqué');
      onRecharger();
    } catch (e) { toast(e.message, 'err'); }
  };

  return (
    <div className="border-t border-slate-200 p-4 space-y-6 bg-slate-50/60">
      {/* Suivi */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Champ label="Statut">
          <select value={chantier.statut} onChange={e => onMaj(chantier.ref, { statut: e.target.value })} className="champ">
            {STATUTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Champ>
        <Champ label="Début"><input type="date" value={chantier.dateDebut || ''} onChange={e => onMaj(chantier.ref, { dateDebut: e.target.value })} className="champ" /></Champ>
        <Champ label="Fin"><input type="date" value={chantier.dateFin || ''} onChange={e => onMaj(chantier.ref, { dateFin: e.target.value })} className="champ" /></Champ>
        <Champ label="Montant TTC"><input type="number" value={chantier.montantTTC || 0} onChange={e => onMaj(chantier.ref, { montantTTC: Number(e.target.value) })} className="champ" /></Champ>
      </div>

      {/* Documents */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[13px] font-semibold text-slate-800">Documents</h3>
          <label className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors ${enCours ? 'bg-slate-100 text-slate-400 border-transparent' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
            <Icone nom="plus" className="w-3.5 h-3.5" />{enCours ? 'Envoi…' : 'Déposer'}
            <input type="file" className="hidden" disabled={enCours} onChange={e => { deposer(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        </div>
        {(chantier.documents || []).length === 0 ? (
          <p className="text-xs text-slate-400 bg-white border border-slate-200 rounded-lg px-3 py-4 text-center">Aucun document.</p>
        ) : (
          <ul className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            {chantier.documents.map(doc => (
              <li key={doc._id} className="flex items-center gap-2 px-3 py-2">
                <button onClick={() => telecharger(doc)} className="flex-1 min-w-0 text-left">
                  <p className="text-sm text-slate-800 truncate hover:text-orange-600">{doc.nom}</p>
                </button>
                <button onClick={() => basculerVisible(doc)}
                  title={doc.visibleClient ? 'Visible par le client — cliquer pour masquer' : 'Interne — cliquer pour le montrer au client'}
                  className="shrink-0">
                  <Mention ton={doc.visibleClient ? 'fait' : 'neutre'}>
                    {doc.visibleClient ? 'visible du client' : 'interne'}
                  </Mention>
                </button>
                <button onClick={() => supprimerDoc(doc)} className="shrink-0 text-slate-300 hover:text-red-500 px-1" aria-label="Supprimer">✕</button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Avancement */}
      <section>
        <h3 className="text-[13px] font-semibold text-slate-800 mb-2">Avancement</h3>
        <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="flex gap-2">
            <input type="date" value={etape.date} onChange={e => setEtape(s => ({ ...s, date: e.target.value }))} className="champ w-40" />
            <input placeholder="Ce qui a été fait" value={etape.titre} onChange={e => setEtape(s => ({ ...s, titre: e.target.value }))} className="champ flex-1" />
            <Bouton variante="primaire" taille="petit" onClick={ajouterEtape} disabled={!etape.titre.trim()}>
              Ajouter
            </Bouton>
          </div>
          <input placeholder="Détail (facultatif) — le client le lit" value={etape.detail} onChange={e => setEtape(s => ({ ...s, detail: e.target.value }))} className="champ w-full" />
        </div>
        {(chantier.etapes || []).length > 0 && (
          <ul className="mt-2 space-y-1">
            {[...chantier.etapes].sort((a, b) => b.date.localeCompare(a.date)).map(e => (
              <li key={e._id} className="text-sm text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2">
                <span className="text-xs text-slate-400 mr-2">{fmtDate(e.date)}</span>
                <span className="font-medium text-slate-800">{e.titre}</span>
                {e.detail && <span className="text-slate-500"> — {e.detail}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {estPatron() && <Factures ref_={chantier.ref} montantHT={chantier.montantHT} toast={toast} />}

      {/* Brief d'équipe */}
      <BriefChantier ref_={chantier.ref} toast={toast} />

      {/* Accès client */}
      <section className="flex flex-wrap items-center gap-2 pt-1">
        <h3 className="text-[13px] font-semibold text-slate-800 mr-auto">
          Accès client
          <span className="ml-2 font-normal text-xs text-slate-400">
            {chantier.acces?.envoyeLe
              ? `envoyé le ${new Date(chantier.acces.envoyeLe).toLocaleDateString('fr-FR')}${chantier.acces.vuLe ? ' · consulté' : ' · pas encore ouvert'}`
              : 'jamais envoyé'}
          </span>
        </h3>
        <Bouton variante="primaire" taille="petit" onClick={envoyerAcces}>
          {chantier.acces?.envoyeLe ? 'Renvoyer le lien' : 'Envoyer le lien'}
        </Bouton>
        {chantier.acces?.envoyeLe && (
          <Bouton variante="danger" taille="petit" onClick={revoquer}>Révoquer</Bouton>
        )}
      </section>
    </div>
  );
}

// Le brief : généré depuis le devis accepté et le dossier de visite, corrigé à
// la main, lu par l'équipe sur téléphone par un lien signé — distinct de l'accès
// client, qui ne voit jamais ce document.
function BriefChantier({ ref_, toast }) {
  const [brief, setBrief] = useState(null);
  const [lien, setLien] = useState(null);
  const [etat, setEtat] = useState('chargement');
  const [occupe, setOccupe] = useState('');
  const [edition, setEdition] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await proFetch(`/chantiers/${ref_}/brief`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Lecture impossible');
      setBrief(d.brief); setLien(d.lien); setEtat('ok');
    } catch { setEtat('erreur'); }
  }, [ref_]);
  useEffect(() => { charger(); }, [charger]);

  const appel = async (quoi, chemin, options, message) => {
    setOccupe(quoi);
    try {
      const r = await proFetch(chemin, options);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      if (d.brief) setBrief(d.brief);
      if (d.lien) setLien(d.lien);
      if (message) toast(message);
      if (quoi === 'generer') await charger();
      return d;
    } catch (e) { toast(e.message, 'err'); return null; }
    finally { setOccupe(''); }
  };

  const generer = () => {
    if (brief && !window.confirm('Regénérer le brief ? Les corrections faites à la main seront remplacées.')) return;
    appel('generer', `/chantiers/${ref_}/brief/generer`, { method: 'POST', body: JSON.stringify({}) }, 'Brief généré en français et en roumain');
  };
  const enregistrer = async ({ fr, ro }) => {
    const d = await appel('enregistrer', `/chantiers/${ref_}/brief`, { method: 'PUT', body: JSON.stringify({ fr, ro }) }, 'Brief enregistré');
    if (d) setEdition(false);
  };
  const retraduire = async ({ fr }) => {
    const d = await appel('enregistrer', `/chantiers/${ref_}/brief`, { method: 'PUT', body: JSON.stringify({ fr }) });
    if (!d) return;
    const t = await appel('traduire', `/chantiers/${ref_}/brief/traduire`, { method: 'POST', body: JSON.stringify({}) }, 'Roumain retraduit depuis le français');
    if (t) setEdition(false);
  };
  const copierLien = async () => {
    try { await navigator.clipboard.writeText(lien); toast('Lien équipe copié'); } catch { toast(lien, 'err'); }
  };
  const revoquer = async () => {
    if (!window.confirm('Révoquer le lien équipe ? Les liens déjà envoyés cesseront de fonctionner ; un nouveau sera créé.')) return;
    await appel('lien', `/chantiers/${ref_}/brief/revoquer`, { method: 'POST' }, 'Ancien lien révoqué, nouveau lien prêt');
  };
  const imprimer = async (langue) => {
    const onglet = window.open('', '_blank');
    try {
      const r = await proFetch(`/chantiers/${ref_}/brief/imprimer?langue=${langue}`);
      if (!r.ok) throw new Error((await r.json()).error || 'Impression impossible');
      onglet.document.write(await r.text()); onglet.document.close();
      setTimeout(() => onglet.print(), 500);
    } catch (e) { onglet?.close(); toast(e.message, 'err'); }
  };
  const jour = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
  const n = (k) => (brief?.fr?.[k] || []).length;

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="text-[13px] font-semibold text-slate-800">
          Brief d'équipe
          {brief && <span className="font-normal text-slate-400"> — généré le {jour(brief.genereLe)}{brief.modifieLe ? `, corrigé le ${jour(brief.modifieLe)}` : ''}{brief.ro ? ' · FR + RO' : ' · FR seulement'}</span>}
        </h3>
        <div className="flex flex-wrap gap-2">
          {!brief && <Bouton variante="primaire" taille="petit" onClick={generer} disabled={!!occupe}>{occupe === 'generer' ? 'Génération… (1 à 2 min)' : 'Générer le brief'}</Bouton>}
          {brief && <>
            <Bouton taille="petit" onClick={() => setEdition(true)} disabled={!!occupe}>Modifier</Bouton>
            <Bouton taille="petit" onClick={() => imprimer('fr')}>Imprimer FR</Bouton>
            <Bouton taille="petit" onClick={() => imprimer('ro')} disabled={!brief.ro}>Imprimer RO</Bouton>
            <Bouton taille="petit" onClick={generer} disabled={!!occupe}>{occupe === 'generer' ? 'Génération…' : 'Regénérer'}</Bouton>
          </>}
        </div>
      </div>
      {etat === 'erreur' && <p className="text-xs text-slate-400">Brief illisible pour le moment.</p>}
      {brief && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
          <p className="font-semibold text-slate-900">{brief.fr?.titre}</p>
          <p className="text-slate-600 mt-0.5">{brief.fr?.resume}</p>
          <p className="text-xs text-slate-400 mt-1.5">
            {n('pieces')} pièce{n('pieces') > 1 ? 's' : ''} · {n('etapes')} étapes · {n('fournitures')} fournitures · {(brief.plans || []).length} plan{(brief.plans || []).length > 1 ? 's' : ''}
            {n('points_ouverts') ? ` · ${n('points_ouverts')} point${n('points_ouverts') > 1 ? 's' : ''} à confirmer` : ''}
          </p>
          {lien && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Lien équipe (FR/RO, 120 jours, sans prix ni documents) :</span>
              <code className="flex-1 min-w-0 truncate text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-600">{lien}</code>
              <Bouton taille="petit" onClick={copierLien}>Copier</Bouton>
              <Bouton taille="petit" variante="discret" onClick={revoquer} disabled={!!occupe}>Révoquer</Bouton>
            </div>
          )}
        </div>
      )}
      {!brief && etat === 'ok' && (
        <p className="text-xs text-slate-400">Pas encore de brief. Il se génère depuis le devis accepté et le dossier de visite, en français puis en roumain — comptez une à deux minutes.</p>
      )}
      {edition && brief && (
        <BriefEditeur brief={brief} occupe={!!occupe} onFermer={() => setEdition(false)} onEnregistrer={enregistrer} onRetraduire={retraduire} />
      )}
    </section>
  );
}

function Factures({ ref_, montantHT, toast }) {
  const [etat, setEtat] = useState(null);
  const [part, setPart] = useState(30);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await proFetch(`/chantiers/${ref_}/factures`);
      setEtat(await r.json());
    } catch { /* la liste se recharge au prochain rendu */ }
  }, [ref_]);
  useEffect(() => { charger(); }, [charger]);

  const appeler = async (chemin, corps) => {
    setBusy(true);
    try {
      const r = await proFetch(`/chantiers/${ref_}/factures${chemin}`, {
        method: 'POST', body: JSON.stringify(corps || {}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      await charger();
      return d;
    } catch (e) { toast(e.message, 'err'); }
    finally { setBusy(false); }
  };

  const voir = (f) => window.open(`/api/pro/chantiers/${ref_}/factures/${f._id}/document`, '_blank', 'noopener');

  if (!etat) return null;
  if (!montantHT) {
    return (
      <section>
        <h3 className="text-[13px] font-semibold text-slate-800 mb-2">Factures</h3>
        <p className="text-xs text-slate-400 bg-white border border-slate-200 rounded-lg px-3 py-3">
          Renseignez le montant du marché ci-dessus pour pouvoir facturer.
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-baseline gap-3 mb-2">
        <h3 className="text-[13px] font-semibold text-slate-800">Factures</h3>
        <span className="text-xs text-slate-400">
          marché {fmtEur(etat.marcheHT)} · émis {fmtEur(etat.emisHT)}
          {etat.brouillonHT > 0 && ` · brouillons ${fmtEur(etat.brouillonHT)}`}
          {' · reste '}{fmtEur(etat.resteAFacturerHT)}
        </span>
      </div>

      {etat.factures.length > 0 && (
        <ul className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 mb-2">
          {etat.factures.map(f => (
            <li key={f._id} className="flex items-center gap-2 px-3 py-2 text-sm">
              <button onClick={() => voir(f)} className="flex-1 min-w-0 text-left hover:text-orange-600">
                <span className="font-medium">{f.numero || 'brouillon'}</span>
                <span className="text-slate-400 ml-2">{f.type}</span>
              </button>
              <span className="tabular-nums text-slate-700">{fmtEur(f.type === 'avoir' ? -f.montantTTC : f.montantTTC)}</span>
              <Etat ton={f.statut === 'payee' ? 'fait' : f.statut === 'emise' ? 'cours' : f.statut === 'annulee' ? 'neutre' : 'attente'}>
                {f.statut === 'payee' ? 'encaissée' : f.statut === 'emise' ? 'émise' : f.statut === 'annulee' ? 'annulée' : 'brouillon'}
              </Etat>
              {f.statut === 'brouillon' && (
                <Bouton variante="sombre" taille="petit" disabled={busy} onClick={() => appeler(`/${f._id}/emettre`)}>Émettre</Bouton>
              )}
              {f.statut === 'emise' && (
                <Bouton taille="petit" disabled={busy} onClick={() => appeler(`/${f._id}/payee`, { moyen: 'virement' })}>Encaissée</Bouton>
              )}
              {f.statut !== 'annulee' && (
                <button disabled={busy}
                        onClick={() => {
                          const brouillon = f.statut === 'brouillon';
                          if (!window.confirm(brouillon ? 'Supprimer ce brouillon ?'
                            : `Annuler ${f.numero} ? Un avoir sera émis — une facture émise ne se supprime pas.`)) return;
                          appeler(`/${f._id}/annuler`, { motif: '' });
                        }}
                        className="text-slate-300 hover:text-red-500 px-1">✕</button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input type="number" min="1" max="100" value={part} onChange={e => setPart(Number(e.target.value))}
               className="champ w-20 text-right" />
        <span className="text-xs text-slate-400">% du marché</span>
        <button disabled={busy || etat.resteAFacturerHT <= 0} onClick={() => appeler('', { type: 'acompte', pourcentage: part / 100 })}
                className="text-xs font-semibold border border-slate-300 hover:border-orange-400 rounded-lg px-3 py-1.5">Acompte</button>
        <button disabled={busy || etat.resteAFacturerHT <= 0} onClick={() => appeler('', { type: 'situation', pourcentage: part / 100 })}
                className="text-xs font-semibold border border-slate-300 hover:border-orange-400 rounded-lg px-3 py-1.5">Situation</button>
        <button disabled={busy || etat.resteAFacturerHT <= 0} onClick={() => appeler('', { type: 'solde' })}
                className="text-xs font-semibold border border-slate-300 hover:border-orange-400 rounded-lg px-3 py-1.5">
          Solde ({fmtEur(etat.resteAFacturerHT)})
        </button>
      </div>
    </section>
  );
}

function Champ({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</span>
      {children}
    </label>
  );
}
